/**
 * Doculegis.ai Terms and Conditions (structured for UI rendering). Doculegis.ai is a product of Lëtzebuerg.ai.
 * Source: Letzebuerg_AI_Terms_and_Conditions PDF — Last Updated: March 16, 2026
 */

export const TERMS_META = {
  displayTitle: "Doculegis.ai Terms and Conditions",
  /** Shown under the title; avoids repeating the product name alongside the heading */
  productOfLine: "A product of Lëtzebuerg.ai",
  lastUpdated: "March 16, 2026",
} as const;

/** Stored on the user profile with `terms_accepted_at` for legal audit; keep in sync with the embedded document. */
export const TERMS_DOCUMENT_VERSION = TERMS_META.lastUpdated;

export function needsTermsAcceptance(
  user:
    | {
        termsAcceptedAt?: Date | string | null;
        termsDocumentVersion?: string | null;
      }
    | null
    | undefined
): boolean {
  if (!user) return false;
  if (!user.termsAcceptedAt) return true;
  return user.termsDocumentVersion !== TERMS_DOCUMENT_VERSION;
}

export type TermsBlock =
  | { type: "subheading"; text: string }
  | { type: "paragraph"; text: string }
  | { type: "lettered"; items: { letter: string; text: string }[] }
  | { type: "bullets"; items: string[] }
  | { type: "caps"; text: string };

export interface TermsSection {
  id: string;
  title: string;
  blocks: TermsBlock[];
}

export const termsAndConditionsSections: TermsSection[] = [
  {
    id: "definitions",
    title: "1. DEFINITIONS AND INTERPRETATION",
    blocks: [
      { type: "subheading", text: "1.1 Definitions" },
      {
        type: "paragraph",
        text: "In these Terms and Conditions, the following terms shall have the meanings set forth below:",
      },
      {
        type: "bullets",
        items: [
          "“Agreement” means these Terms and Conditions together with any Order Form, Statement of Work, or other document expressly incorporated by reference.",
          "“AI Services” means the artificial intelligence solutions, services, and related deliverables provided by the Company to the Client as described in the applicable Order Form or Statement of Work.",
          "“Client” means the individual or legal entity entering into this Agreement with the Company for the provision of AI Services.",
          "“Client Data” means all data, information, materials, and content provided by the Client to the Company for the purpose of delivering the AI Services.",
          "“Confidential Information” means all non-public information disclosed by either party to the other, whether orally or in writing, that is designated as confidential or that reasonably should be understood to be confidential.",
          "“Company” means Lëtzebuerg.ai, a company incorporated under the laws of Luxembourg, with registered office in Luxembourg.",
          "“Deliverables” means any work product, documents, models, reports, software, or other materials to be delivered by the Company to the Client under this Agreement.",
          "“Intellectual Property Rights” means all patents, copyrights, trademarks, trade secrets, know-how, database rights, and other intellectual property rights, whether registered or unregistered.",
          "“Order Form” means the document executed by both parties specifying the AI Services to be provided, fees, timelines, and other specific terms.",
          "“Personal Data” means any information relating to an identified or identifiable natural person as defined in the GDPR.",
          "“Services Commencement Date” means the date specified in the Order Form or the date on which the Company begins providing the AI Services.",
          "“Statement of Work” or “SOW” means a document describing the scope, deliverables, timeline, and other specifics of a particular project or engagement.",
        ],
      },
      { type: "subheading", text: "1.2 Interpretation" },
      {
        type: "bullets",
        items: [
          "References to statutory provisions include those provisions as amended or re-enacted.",
          "Headings are for convenience only and do not affect interpretation.",
          "Words in the singular include the plural and vice versa.",
          "References to “writing” include email and other electronic communications.",
        ],
      },
    ],
  },
  {
    id: "scope",
    title: "2. SCOPE OF SERVICES",
    blocks: [
      { type: "subheading", text: "2.1 Services Provided" },
      {
        type: "paragraph",
        text: "The Company shall provide AI Services to the Client as described in the applicable Order Form or Statement of Work, which may include but are not limited to:",
      },
      {
        type: "lettered",
        items: [
          { letter: "a", text: "AI strategy consulting and assessment;" },
          { letter: "b", text: "Custom AI model development and training;" },
          { letter: "c", text: "AI integration services;" },
          { letter: "d", text: "Machine learning solutions;" },
          { letter: "e", text: "Data analysis and insights;" },
          { letter: "f", text: "AI implementation support;" },
          { letter: "g", text: "Training and documentation;" },
          { letter: "h", text: "Ongoing support and maintenance." },
        ],
      },
      { type: "subheading", text: "2.2 Service Specifications" },
      {
        type: "paragraph",
        text: "The specific scope, deliverables, timelines, and fees for each engagement shall be set forth in an Order Form or Statement of Work executed by both parties.",
      },
      { type: "subheading", text: "2.3 Changes to Services" },
      {
        type: "paragraph",
        text: "Any changes to the scope of Services must be agreed upon in writing by both parties through a change order or amendment to the applicable SOW.",
      },
    ],
  },
  {
    id: "client-obligations",
    title: "3. CLIENT OBLIGATIONS",
    blocks: [
      { type: "subheading", text: "3.1 Cooperation" },
      {
        type: "paragraph",
        text: "The Client shall:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Provide timely access to Client Data, systems, personnel, and facilities reasonably required for the Company to perform the Services;",
          },
          {
            letter: "b",
            text: "Designate a primary point of contact with authority to make decisions regarding the Services;",
          },
          {
            letter: "c",
            text: "Provide accurate, complete, and timely information and feedback;",
          },
          {
            letter: "d",
            text: "Review and approve Deliverables within the timeframes specified in the SOW;",
          },
          {
            letter: "e",
            text: "Ensure that all Client Data provided complies with applicable laws and regulations.",
          },
        ],
      },
      { type: "subheading", text: "3.2 Client Data Responsibilities" },
      {
        type: "paragraph",
        text: "The Client represents and warrants that:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "It has all necessary rights, licenses, and consents to provide Client Data to the Company;",
          },
          {
            letter: "b",
            text: "The Client Data does not infringe upon any third-party Intellectual Property Rights;",
          },
          {
            letter: "c",
            text: "The processing of Client Data by the Company as instructed will not violate any applicable laws, including data protection laws.",
          },
        ],
      },
      { type: "subheading", text: "3.3 Client Systems" },
      {
        type: "paragraph",
        text: "The Client is responsible for maintaining appropriate IT infrastructure, security measures, and backup procedures for its own systems and data.",
      },
    ],
  },
  {
    id: "fees",
    title: "4. FEES AND PAYMENT",
    blocks: [
      { type: "subheading", text: "4.1 Fees" },
      {
        type: "paragraph",
        text: "The Client shall pay the Company the fees specified in the applicable Order Form or SOW. Unless otherwise specified, all fees are quoted in EUR (Euros).",
      },
      { type: "subheading", text: "4.2 Payment Terms" },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Invoices shall be issued in accordance with the payment schedule set forth in the Order Form or SOW;",
          },
          {
            letter: "b",
            text: "Payment is due within thirty (30) days of the invoice date unless otherwise specified;",
          },
          {
            letter: "c",
            text: "Payments shall be made by bank transfer to the account specified on the invoice;",
          },
          {
            letter: "d",
            text: "All fees are exclusive of applicable taxes, which shall be added to invoiced amounts as required by law.",
          },
        ],
      },
      { type: "subheading", text: "4.3 Late Payment" },
      {
        type: "paragraph",
        text: "Late payments shall accrue interest at the rate of 8% per annum above the base rate of the European Central Bank, or the maximum rate permitted by law, whichever is lower.",
      },
      { type: "subheading", text: "4.4 Expenses" },
      {
        type: "paragraph",
        text: "Unless otherwise agreed in the SOW, the Client shall reimburse the Company for reasonable, pre-approved expenses incurred in connection with providing the Services.",
      },
      { type: "subheading", text: "4.5 Fee Adjustments" },
      {
        type: "paragraph",
        text: "The Company reserves the right to adjust fees annually or upon renewal, with at least sixty (60) days’ prior written notice to the Client.",
      },
    ],
  },
  {
    id: "ip",
    title: "5. INTELLECTUAL PROPERTY RIGHTS",
    blocks: [
      { type: "subheading", text: "5.1 Pre-Existing IP" },
      {
        type: "paragraph",
        text: "Each party retains all rights to its pre-existing Intellectual Property Rights.",
      },
      { type: "subheading", text: "5.2 Client Data" },
      {
        type: "paragraph",
        text: "As between the parties, the Client retains all rights, title, and interest in and to the Client Data. The Client grants the Company a non-exclusive, worldwide, royalty-free license to use, reproduce, and process Client Data solely to the extent necessary to provide the Services.",
      },
      { type: "subheading", text: "5.3 Deliverables" },
      {
        type: "paragraph",
        text: "Unless otherwise specified in the Order Form or SOW:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Work Product: Upon full payment of all fees, the Client shall own all rights, title, and interest in custom Deliverables specifically created for the Client under the Agreement;",
          },
          {
            letter: "b",
            text: "Company Tools: The Company retains all rights to its proprietary methodologies, tools, frameworks, algorithms, models, and know-how used to provide the Services;",
          },
          {
            letter: "c",
            text: "Anonymized Data: The Company may retain and use anonymized, de-identified, and aggregated data derived from the Services for its own business purposes, including improving its AI models and services.",
          },
        ],
      },
      { type: "subheading", text: "5.4 Third-Party Components" },
      {
        type: "paragraph",
        text: "Deliverables may include third-party open-source or licensed components, which remain subject to their respective licenses.",
      },
      { type: "subheading", text: "5.5 Restrictions" },
      {
        type: "paragraph",
        text: "Neither party shall use the other party’s Intellectual Property Rights except as expressly permitted in this Agreement.",
      },
    ],
  },
  {
    id: "confidentiality",
    title: "6. CONFIDENTIALITY",
    blocks: [
      { type: "subheading", text: "6.1 Confidentiality Obligations" },
      {
        type: "paragraph",
        text: "Each party agrees to:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Maintain the confidentiality of all Confidential Information received from the other party;",
          },
          {
            letter: "b",
            text: "Use Confidential Information solely for purposes of performing or receiving the Services;",
          },
          {
            letter: "c",
            text: "Disclose Confidential Information only to employees, contractors, and advisors who need to know and who are bound by confidentiality obligations at least as protective as those in this Agreement;",
          },
          {
            letter: "d",
            text: "Protect Confidential Information using at least the same degree of care it uses for its own confidential information, but no less than reasonable care.",
          },
        ],
      },
      { type: "subheading", text: "6.2 Exceptions" },
      {
        type: "paragraph",
        text: "Confidentiality obligations do not apply to information that:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Is or becomes publicly available through no breach of this Agreement;",
          },
          {
            letter: "b",
            text: "Was rightfully known to the receiving party prior to disclosure;",
          },
          {
            letter: "c",
            text: "Is rightfully obtained from a third party without breach of confidentiality obligations;",
          },
          {
            letter: "d",
            text: "Is independently developed without use of or reference to the Confidential Information;",
          },
          {
            letter: "e",
            text: "Must be disclosed pursuant to legal or regulatory requirements, provided the receiving party provides prompt notice to enable the disclosing party to seek protective measures.",
          },
        ],
      },
      { type: "subheading", text: "6.3 Duration" },
      {
        type: "paragraph",
        text: "Confidentiality obligations shall survive for five (5) years following termination or expiration of this Agreement.",
      },
    ],
  },
  {
    id: "data-protection",
    title: "7. DATA PROTECTION AND PRIVACY",
    blocks: [
      { type: "subheading", text: "7.1 GDPR Compliance" },
      {
        type: "paragraph",
        text: "Both parties shall comply with all applicable data protection laws, including the General Data Protection Regulation (EU) 2016/679 (“GDPR”) and Luxembourg data protection law.",
      },
      { type: "subheading", text: "7.2 Data Processing Agreement" },
      {
        type: "paragraph",
        text: "To the extent the Company processes Personal Data on behalf of the Client, the parties shall execute a separate Data Processing Agreement (“DPA”) in accordance with Article 28 of the GDPR.",
      },
      { type: "subheading", text: "7.3 Data Controller and Processor" },
      {
        type: "paragraph",
        text: "Where applicable:",
      },
      {
        type: "lettered",
        items: [
          { letter: "a", text: "The Client acts as the Data Controller;" },
          { letter: "b", text: "The Company acts as the Data Processor;" },
          {
            letter: "c",
            text: "The Company shall process Personal Data only on documented instructions from the Client;",
          },
          {
            letter: "d",
            text: "The Company shall implement appropriate technical and organizational measures to ensure security of Personal Data.",
          },
        ],
      },
      { type: "subheading", text: "7.4 Data Transfers" },
      {
        type: "paragraph",
        text: "Any transfer of Personal Data outside the European Economic Area shall be subject to appropriate safeguards as required by the GDPR.",
      },
      { type: "subheading", text: "7.5 Data Subject Rights" },
      {
        type: "paragraph",
        text: "The Company shall, to the extent legally permitted, promptly notify the Client if it receives any request from a data subject to exercise their rights under the GDPR and shall provide reasonable assistance to the Client in responding to such requests.",
      },
    ],
  },
  {
    id: "warranties",
    title: "8. WARRANTIES AND DISCLAIMERS",
    blocks: [
      { type: "subheading", text: "8.1 Company Warranties" },
      {
        type: "paragraph",
        text: "The Company warrants that:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "It has the right and authority to enter into this Agreement and perform the Services;",
          },
          {
            letter: "b",
            text: "The Services will be performed in a professional and workmanlike manner consistent with industry standards;",
          },
          {
            letter: "c",
            text: "It will comply with all applicable laws and regulations in performing the Services;",
          },
          {
            letter: "d",
            text: "To its knowledge, the Deliverables will not infringe any third-party Intellectual Property Rights.",
          },
        ],
      },
      { type: "subheading", text: "8.2 Client Warranties" },
      {
        type: "paragraph",
        text: "The Client warrants that:",
      },
      {
        type: "lettered",
        items: [
          { letter: "a", text: "It has the right and authority to enter into this Agreement;" },
          {
            letter: "b",
            text: "It has all necessary rights and consents to provide Client Data to the Company;",
          },
          {
            letter: "c",
            text: "The Client Data and its use by the Company as instructed will not violate any applicable laws or third-party rights.",
          },
        ],
      },
      { type: "subheading", text: "8.3 Disclaimer" },
      {
        type: "caps",
        text: "EXCEPT AS EXPRESSLY SET FORTH IN THIS AGREEMENT, THE COMPANY MAKES NO WARRANTIES, EXPRESS OR IMPLIED, INCLUDING BUT NOT LIMITED TO WARRANTIES OF MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, OR NON-INFRINGEMENT. THE COMPANY DOES NOT WARRANT THAT THE SERVICES WILL BE UNINTERRUPTED, ERROR-FREE, OR THAT ALL ERRORS WILL BE CORRECTED.",
      },
      { type: "subheading", text: "8.4 AI Services Disclaimer" },
      {
        type: "paragraph",
        text: "The Client acknowledges that:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "AI and machine learning models are probabilistic and may produce unexpected or inaccurate results;",
          },
          {
            letter: "b",
            text: "The performance of AI Services depends on the quality, completeness, and representativeness of training data;",
          },
          {
            letter: "c",
            text: "AI models may require ongoing training, tuning, and maintenance;",
          },
          {
            letter: "d",
            text: "The Company does not guarantee specific outcomes, performance levels, or business results from the AI Services unless expressly specified in the SOW.",
          },
        ],
      },
    ],
  },
  {
    id: "liability",
    title: "9. LIMITATION OF LIABILITY",
    blocks: [
      { type: "subheading", text: "9.1 Liability Cap" },
      {
        type: "caps",
        text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, THE COMPANY’S TOTAL AGGREGATE LIABILITY ARISING OUT OF OR RELATED TO THIS AGREEMENT, WHETHER IN CONTRACT, TORT (INCLUDING NEGLIGENCE), OR OTHERWISE, SHALL NOT EXCEED THE TOTAL FEES PAID BY THE CLIENT TO THE COMPANY IN THE TWELVE (12) MONTHS PRECEDING THE EVENT GIVING RISE TO THE CLAIM.",
      },
      { type: "subheading", text: "9.2 Exclusion of Consequential Damages" },
      {
        type: "caps",
        text: "TO THE MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, NEITHER PARTY SHALL BE LIABLE FOR ANY INDIRECT, INCIDENTAL, SPECIAL, CONSEQUENTIAL, OR PUNITIVE DAMAGES, INCLUDING BUT NOT LIMITED TO LOSS OF PROFITS, LOSS OF REVENUE, LOSS OF DATA, LOSS OF BUSINESS OPPORTUNITIES, OR COST OF REPLACEMENT SERVICES, EVEN IF ADVISED OF THE POSSIBILITY OF SUCH DAMAGES.",
      },
      { type: "subheading", text: "9.3 Exceptions" },
      {
        type: "paragraph",
        text: "The limitations in Sections 9.1 and 9.2 shall not apply to:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Either party’s breach of confidentiality obligations;",
          },
          {
            letter: "b",
            text: "Either party’s infringement of the other party’s Intellectual Property Rights;",
          },
          {
            letter: "c",
            text: "Liability arising from fraud, willful misconduct, or gross negligence;",
          },
          {
            letter: "d",
            text: "Liability that cannot be excluded or limited under applicable law.",
          },
        ],
      },
      { type: "subheading", text: "9.4 Allocation of Risk" },
      {
        type: "paragraph",
        text: "The parties acknowledge that the limitations of liability in this Section 9 reflect the allocation of risk between the parties and form an essential basis of the bargain.",
      },
    ],
  },
  {
    id: "indemnification",
    title: "10. INDEMNIFICATION",
    blocks: [
      { type: "subheading", text: "10.1 Company Indemnification" },
      {
        type: "paragraph",
        text: "The Company shall defend, indemnify, and hold harmless the Client from and against any claims, damages, losses, and expenses (including reasonable attorneys’ fees) arising from:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Breach of the Company’s representations or warranties;",
          },
          {
            letter: "b",
            text: "Infringement by the Deliverables of any third-party Intellectual Property Rights, provided the Client promptly notifies the Company of the claim and cooperates in the defense.",
          },
        ],
      },
      { type: "subheading", text: "10.2 Client Indemnification" },
      {
        type: "paragraph",
        text: "The Client shall defend, indemnify, and hold harmless the Company from and against any claims, damages, losses, and expenses (including reasonable attorneys’ fees) arising from:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Client Data or the Client’s use of the Deliverables;",
          },
          {
            letter: "b",
            text: "Breach of the Client’s representations or warranties;",
          },
          {
            letter: "c",
            text: "The Client’s violation of applicable laws or third-party rights.",
          },
        ],
      },
      { type: "subheading", text: "10.3 Indemnification Procedures" },
      {
        type: "paragraph",
        text: "The indemnified party shall:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "Promptly notify the indemnifying party of any claim;",
          },
          {
            letter: "b",
            text: "Provide reasonable cooperation in the defense;",
          },
          {
            letter: "c",
            text: "Allow the indemnifying party to control the defense and settlement, provided that no settlement shall be made without the indemnified party’s consent if it imposes obligations on the indemnified party.",
          },
        ],
      },
    ],
  },
  {
    id: "term",
    title: "11. TERM AND TERMINATION",
    blocks: [
      { type: "subheading", text: "11.1 Term" },
      {
        type: "paragraph",
        text: "This Agreement commences on the Services Commencement Date and continues until all SOWs are completed or until terminated in accordance with this Section 11.",
      },
      { type: "subheading", text: "11.2 Termination for Convenience" },
      {
        type: "paragraph",
        text: "Either party may terminate this Agreement or any SOW for convenience upon sixty (60) days’ prior written notice to the other party.",
      },
      { type: "subheading", text: "11.3 Termination for Cause" },
      {
        type: "paragraph",
        text: "Either party may terminate this Agreement or any SOW immediately upon written notice if:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "The other party materially breaches this Agreement and fails to cure such breach within thirty (30) days of receiving written notice;",
          },
          {
            letter: "b",
            text: "The other party becomes insolvent, makes an assignment for the benefit of creditors, or has a receiver or administrator appointed.",
          },
        ],
      },
      { type: "subheading", text: "11.4 Effect of Termination" },
      {
        type: "paragraph",
        text: "Upon termination or expiration:",
      },
      {
        type: "lettered",
        items: [
          {
            letter: "a",
            text: "The Client shall pay all fees for Services performed up to the termination date and any non-cancellable expenses incurred;",
          },
          {
            letter: "b",
            text: "The Company shall deliver all completed Deliverables and work-in-progress in its possession;",
          },
          {
            letter: "c",
            text: "Each party shall return or destroy all Confidential Information of the other party;",
          },
          {
            letter: "d",
            text: "Sections 4 (Fees), 5 (Intellectual Property), 6 (Confidentiality), 7 (Data Protection), 8.3-8.4 (Disclaimers), 9 (Limitation of Liability), 10 (Indemnification), and 12 (General Provisions) shall survive.",
          },
        ],
      },
      { type: "subheading", text: "11.5 Refund" },
      {
        type: "paragraph",
        text: "If the Client terminates for convenience, no refund of fees already paid shall be due. If the Company terminates for convenience, the Company shall refund any fees paid for Services not yet performed.",
      },
    ],
  },
  {
    id: "general",
    title: "12. GENERAL PROVISIONS",
    blocks: [
      { type: "subheading", text: "12.1 Entire Agreement" },
      {
        type: "paragraph",
        text: "This Agreement, together with any Order Forms, SOWs, and incorporated documents, constitutes the entire agreement between the parties and supersedes all prior or contemporaneous understandings, whether written or oral.",
      },
      { type: "subheading", text: "12.2 Amendments" },
      {
        type: "paragraph",
        text: "This Agreement may only be amended by a written document signed by authorized representatives of both parties.",
      },
      { type: "subheading", text: "12.3 Assignment" },
      {
        type: "paragraph",
        text: "Neither party may assign this Agreement without the prior written consent of the other party, except that either party may assign this Agreement to a successor in connection with a merger, acquisition, or sale of all or substantially all of its assets.",
      },
      { type: "subheading", text: "12.4 Independent Contractors" },
      {
        type: "paragraph",
        text: "The parties are independent contractors. Nothing in this Agreement creates a partnership, joint venture, agency, or employment relationship.",
      },
      { type: "subheading", text: "12.5 Force Majeure" },
      {
        type: "paragraph",
        text: "Neither party shall be liable for delays or failures in performance resulting from causes beyond its reasonable control, including but not limited to acts of God, war, terrorism, riots, pandemic, governmental actions, natural disasters, or internet/telecommunications failures.",
      },
      { type: "subheading", text: "12.6 Notices" },
      {
        type: "paragraph",
        text: "All notices under this Agreement shall be in writing and delivered to the addresses specified in the Order Form or as otherwise notified. Notices shall be deemed given when delivered personally, sent by confirmed email, or three (3) days after mailing by registered mail.",
      },
      { type: "subheading", text: "12.7 Waiver" },
      {
        type: "paragraph",
        text: "No waiver of any provision of this Agreement shall be deemed or constitute a waiver of any other provision, nor shall any waiver constitute a continuing waiver.",
      },
      { type: "subheading", text: "12.8 Severability" },
      {
        type: "paragraph",
        text: "If any provision of this Agreement is held to be invalid or unenforceable, the remaining provisions shall continue in full force and effect, and the invalid provision shall be modified to the minimum extent necessary to make it valid and enforceable.",
      },
      { type: "subheading", text: "12.9 Governing Law" },
      {
        type: "paragraph",
        text: "This Agreement shall be governed by and construed in accordance with the laws of the Grand Duchy of Luxembourg, without regard to its conflicts of law principles.",
      },
      { type: "subheading", text: "12.10 Jurisdiction" },
      {
        type: "paragraph",
        text: "The parties irrevocably submit to the exclusive jurisdiction of the courts of Luxembourg City, Luxembourg, for any dispute arising out of or relating to this Agreement.",
      },
      { type: "subheading", text: "12.11 Language" },
      {
        type: "paragraph",
        text: "This Agreement is executed in English. If this Agreement is translated into any other language, the English version shall prevail in case of any conflict or inconsistency.",
      },
      { type: "subheading", text: "12.12 Publicity" },
      {
        type: "paragraph",
        text: "Neither party may use the other party’s name, logo, or trademarks in marketing materials or public announcements without prior written consent, except that the Company may identify the Client as a customer in its client list with prior consent.",
      },
      { type: "subheading", text: "12.13 Counterparts" },
      {
        type: "paragraph",
        text: "This Agreement may be executed in counterparts, each of which shall be deemed an original and all of which together shall constitute one instrument. Electronic signatures shall be deemed equivalent to original signatures.",
      },
    ],
  },
  {
    id: "acceptance",
    title: "13. ACCEPTANCE",
    blocks: [
      {
        type: "paragraph",
        text: "By executing an Order Form or Statement of Work that references these Terms and Conditions, or by accepting delivery of the Services, the Client agrees to be bound by these Terms and Conditions.",
      },
    ],
  },
  {
    id: "contact",
    title: "CONTACT INFORMATION",
    blocks: [
      {
        type: "paragraph",
        text: "Lëtzebuerg.ai — For questions regarding these Terms and Conditions, please contact:",
      },
      {
        type: "bullets",
        items: [
          "Email: legal@letzebuerg.ai",
          "Address: 38 Rte d'Esch, 1470 Hollerich, Luxembourg",
          "Website: www.letzebuerg.ai",
        ],
      },
      {
        type: "paragraph",
        text: "END OF TERMS AND CONDITIONS",
      },
    ],
  },
];
